import React, {useState} from 'react';

import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {populateSearchParamWithDefaultSort} from 'search/getSortsOrDefault';
import * as SavedSearchesActions from 'search/saved-searches/SavedSearchesActions';
import SavedSearchSettingsModal, {
  SavedSearchSettingsModalModifiableFields,
} from 'search/saved-searches/SavedSearchSettingsModal';
import {
  NonPaginatedSearchParams,
  SAVED_SEARCH_SECTION_TO_SEARCH_TYPE,
  SavedSearch,
} from 'search/saved-searches/SavedSearchUtil';
import {SearchParams} from 'search/SearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

export interface SaveSearchButtonProps {
  searchSection: SearchSection;
  onSaved: (savedSearch: SavedSearch) => void;
  searchParams: SearchParams;
  activeSavedSearch?: SavedSearch;
  disabled?: boolean;
}

export default function SaveSearchButton(props: SaveSearchButtonProps): JSX.Element | null {
  const [submitting, setSubmitting] = useState(false);

  const searchParams = populateSearchParamWithDefaultSort(
    props.searchParams,
    SAVED_SEARCH_SECTION_TO_SEARCH_TYPE[props.searchSection]
  );

  async function saveSearch(title: string): Promise<void> {
    const newSearchParams: NonPaginatedSearchParams = {
      sorts: searchParams.sorts,
      filters: searchParams.filters,
      query: searchParams.query,
    };

    if (searchParams.groupBy) {
      newSearchParams.groupBy = searchParams.groupBy;
    }

    if (props.activeSavedSearch) {
      setSubmitting(true);
      try {
        const updatedSavedSearch = await SavedSearchesActions.update(props.activeSavedSearch.id, {
          searchParams: newSearchParams,
        });
        props.onSaved(updatedSavedSearch);
      } finally {
        setSubmitting(false);
      }
    } else {
      await runModal(SavedSearchSettingsModal, {
        title,
        onRequestSubmit: async (values: SavedSearchSettingsModalModifiableFields) => {
          const savedSearch = await SavedSearchesActions.create({
            name: values.name,
            description: values.description,
            section: props.searchSection,
            params: newSearchParams,
            collaboratorsJson: {collaborators: values.collaboratorsJson ?? []},
          });
          props.onSaved(savedSearch);
        },
      });
    }
  }

  const text = props.activeSavedSearch ? 'Update' : 'Save';

  return (
    <Button
      size={ButtonSize.SMALL}
      buttonStyle={ButtonStyle.SECONDARY}
      text={text}
      onClick={() => {
        saveSearch('Saved Search Settings');
      }}
      disabled={props.disabled || submitting}
      dataTestAttrs={{component: 'SaveSearchButton', element: 'saveLink'}}
    />
  );
}
